/**
 * Platform deep-dive - /sales/platforms (SalesHubPage renders only the tabs above it).
 *
 *   <SalesPlatformsPage />                   owns its scope: renders the h1 and SalesControlBar itself
 *   <SalesPlatformsPage scope={scope} setScope={setScope} />   parent owns the header and control bar
 *
 * Loads GET /api/sales-hub/platforms for the page scope. Left: one selectable card per platform with
 * sales (human name, platform colour, revenue, share, ₹ change, ASP, discount, return rate, sparkline).
 * Right: the selected platform's KPIs, daily trend (Revenue/Units), top 5 products (open
 * ProductDetailDrawer limited to that platform) and "Filter page to this platform" (setScope).
 * Below: a sortable comparison table of every platform; a row selects that platform.
 * Platforms are fetched without the platform filter, so filtering the page to one platform keeps
 * every card and table row (the filtered one is marked) instead of collapsing to a single platform.
 * avg_discount_pct < 0 means sold above list price and is shown as "x% above list".
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Filter, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { LoadError } from '../components/LoadError';
import { SortHeader, useTableControls } from '../components/TableControls';
import { platformColor, platformLabel } from '../config/platforms';
import { useSalesData } from '../components/sales/salesApi';
import { DeltaBadge, formatDelta } from '../components/sales/DeltaBadge';
import { ShareBar } from '../components/sales/ShareBar';
import { Sparkline } from '../components/sales/Sparkline';
import { ProductDetailDrawer } from '../components/sales/ProductDetailDrawer';
import { compareWords, dayCount, formatDateRange, number, shortMoney, wholeMoney } from '../components/sales/format';
import {
  CLEAR_FILTERS, CardsSkeleton, ChangePct, EmptyState, MetricToggle, ViewHeader, hasFilters, pctText, shortDate, usePageScope,
  useSalesOptions,
} from '../components/sales/views/common';

import { useChannels } from '../hooks/useChannels';
const SORT_VALUE = {
  name: row => (row.label || platformLabel(row.platform)).toLowerCase(),
  revenue_change: row => (!row.previous_revenue && row.revenue > 0 ? Number.POSITIVE_INFINITY : row.revenue_change),
};

/** "5.0% off" / "5.0% below list"; a negative discount means the average sale was above list price. */
const discountText = (value, offWord = 'off') => {
  if (value == null || Number.isNaN(Number(value))) return null;
  return Number(value) < 0 ? `${pctText(Math.abs(value))} above list` : `${pctText(value)} ${offWord}`;
};

const FilteredTag = () => (
  <span className="ml-1.5 inline-flex shrink-0 rounded-full bg-blue-50 px-1.5 text-[10px] font-semibold uppercase tracking-wide text-blue-800 ring-1 ring-inset ring-blue-200"
    title="The page is filtered to this platform">Filter</span>
);

const Stat = ({ label, value, children }) => (
  <div className="min-w-0 rounded-lg border border-gray-200 p-3">
    <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</div>
    <div className="mt-0.5 truncate text-lg font-semibold tabular-nums text-gray-900">{value}</div>
    {children && <div className="mt-1 text-xs text-gray-600">{children}</div>}
  </div>
);

const PlatformCard = ({ item, active, filtered, onSelect }) => {
  const name = item.label || platformLabel(item.platform);
  const color = platformColor(item.platform);
  const facts = [
    item.avg_selling_price ? `ASP ${wholeMoney(item.avg_selling_price)}` : null,
    discountText(item.avg_discount_pct),
    item.returned_units ? `${pctText(item.return_rate)} returns` : null,
  ].filter(Boolean).join(' · ');
  return (
    <button type="button" onClick={onSelect} aria-pressed={active} aria-label={`Show ${name} details`}
      className={`relative block w-full overflow-hidden rounded-xl border bg-white p-3 pl-4 text-left shadow-sm transition hover:border-blue-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${active ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'}`}
      data-testid={`platform-card-${item.platform}`}>
      <span className="absolute inset-y-0 left-0 w-1" style={{ background: color }} aria-hidden="true" />
      <div className="flex items-baseline justify-between gap-2">
        <span className="flex min-w-0 items-center"><span className="truncate text-sm font-semibold text-gray-900">{name}</span>{filtered && <FilteredTag />}</span>
        <span className="shrink-0 text-xs tabular-nums text-gray-500">{pctText(item.share)} share</span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <span className="text-lg font-semibold tabular-nums text-gray-900">{shortMoney(item.revenue)}</span>
        <DeltaBadge current={item.revenue} previous={item.previous_revenue} size="xs" />
      </div>
      {facts && <p className="mt-0.5 truncate text-xs text-gray-600">{facts}</p>}
      <Sparkline data={item.trend} dataKey="revenue" color={color} height={24} className="mt-2" />
    </button>
  );
};

const TrendChart = ({ trend, metric, color, name }) => {
  const fmt = value => (metric === 'revenue' ? shortMoney(value) : number(value));
  return (
    <div role="img" aria-label={`Daily ${metric} on ${name} for the selected period`} className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={trend} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 6" vertical={false} stroke="#e5e7eb" />
          <XAxis dataKey="date" tickFormatter={shortDate} minTickGap={24} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis width={56} tickFormatter={fmt} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip labelFormatter={label => formatDateRange(label, label, true)} formatter={value => [fmt(value), metric === 'revenue' ? 'Revenue' : 'Units']} cursor={{ fill: '#f1f5f9' }} />
          <Bar dataKey={metric} fill={color} radius={[3, 3, 0, 0]} maxBarSize={22} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default function SalesPlatformsPage(props) {
  useChannels();   // re-render when channel names or colours change (Settings → Channels)
  const { scope, setScope, salesScope } = usePageScope(props);
  // Every platform stays listed; the page's platform filter only selects and marks one.
  const { data, loading, error, refresh } = useSalesData('platforms', scope, { platform: null });
  const options = useSalesOptions(scope.country, Boolean(salesScope));
  const [picked, setPicked] = useState(null);
  // A platform applied from anywhere (control bar, drawer, button) becomes the selection.
  useEffect(() => { if (scope.platform !== 'ALL') setPicked(null); }, [scope.platform]);
  const [metric, setMetric] = useState('revenue');
  const [openSku, setOpenSku] = useState(null);
  const vs = compareWords(scope.compare, dayCount(scope.date_from, scope.date_to));

  const list = useMemo(() => (data?.platforms || []).filter(item => (item.revenue || 0) > 0), [data]);
  const wanted = picked || (scope.platform !== 'ALL' ? scope.platform : null);
  const selected = list.find(item => item.platform === wanted) || list[0] || null;
  const table = useTableControls(list, { initialSort: { key: 'revenue', dir: 'desc' }, pageSize: 100, sortValue: SORT_VALUE });

  const header = (
    <ViewHeader salesScope={salesScope} title="Platform performance" testId="sales-platforms-heading"
      brands={options.brands} categories={options.categories} onRefresh={refresh} loading={loading} />
  );

  if (error && !data) {
    return (
      <div className="space-y-6" data-testid="sales-platforms-page">
        {header}
        <LoadError what="platform sales" error={error} onRetry={refresh} retrying={loading} testId="sales-platforms-error" />
      </div>
    );
  }

  if (data && !list.length) {
    return (
      <div className="space-y-6" data-testid="sales-platforms-page">
        {header}
        <EmptyState title="No platform sales in this period" testId="sales-platforms-empty"
          action={hasFilters(scope) && <Button size="sm" variant="outline" onClick={() => setScope(CLEAR_FILTERS)}>Clear filters</Button>}>
          {hasFilters(scope) ? 'Nothing matches these filters. Clear them or pick a longer period.' : 'Pick a longer period, or upload sales from the overview tab.'}
        </EmptyState>
      </div>
    );
  }

  const name = selected ? (selected.label || platformLabel(selected.platform)) : '';
  const color = selected ? platformColor(selected.platform) : '#2563EB';
  const pageFiltered = selected && scope.platform === selected.platform;
  const hasTrend = selected?.trend?.some(day => day.revenue || day.units);
  const topSkus = selected?.top_skus || [];

  return (
    <div className="space-y-6" data-testid="sales-platforms-page">
      {header}
      {error && <LoadError what="the latest platform numbers" error={error} onRetry={refresh} retrying={loading} />}

      <div className={`grid gap-4 transition-opacity lg:grid-cols-3 ${loading && data ? 'opacity-60' : ''}`} aria-busy={loading}>
        <section aria-label="Platforms" className="min-w-0">
          <h2 className="mb-2 text-sm font-semibold text-gray-900">Platforms <span className="font-normal text-gray-500">· by revenue</span></h2>
          {!data ? <CardsSkeleton count={4} label="Loading platforms" /> : (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
              {list.map(item => (
                <PlatformCard key={item.platform} item={item} active={selected?.platform === item.platform} filtered={scope.platform === item.platform} onSelect={() => setPicked(item.platform)} />
              ))}
            </div>
          )}
        </section>

        <section className="min-w-0 rounded-xl border border-gray-200 bg-white p-4 lg:col-span-2" aria-labelledby="platform-detail-title" data-testid="platform-detail">
          {!selected ? (
            <div className="space-y-3" aria-busy="true" aria-label="Loading platform details">
              <Skeleton className="h-6 w-48" /><div className="grid grid-cols-2 gap-2 sm:grid-cols-4">{[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-20" />)}</div><Skeleton className="h-56" />
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 id="platform-detail-title" className="flex items-center gap-2 text-base font-semibold text-gray-900">
                  <span className="h-3 w-3 rounded-full" style={{ background: color }} aria-hidden="true" />{name}
                </h2>
                {pageFiltered ? (
                  <Button size="sm" variant="outline" onClick={() => { setScope({ platform: '' }); setPicked(selected.platform); }} data-testid="platform-clear-filter">
                    <X className="mr-1.5 h-4 w-4" aria-hidden="true" />Show all platforms
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setScope({ platform: selected.platform })} data-testid="platform-apply-filter">
                    <Filter className="mr-1.5 h-4 w-4" aria-hidden="true" />Filter page to this platform
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Stat label="Revenue" value={shortMoney(selected.revenue)}>
                  <DeltaBadge current={selected.revenue} previous={selected.previous_revenue} size="xs" />
                </Stat>
                <Stat label="Units" value={number(selected.units)}>
                  {selected.previous_units != null ? <DeltaBadge current={selected.units} previous={selected.previous_units} kind="number" size="xs" /> : null}
                </Stat>
                <Stat label="Avg price" value={selected.avg_selling_price ? wholeMoney(selected.avg_selling_price) : '—'}>
                  {discountText(selected.avg_discount_pct, 'below list') || 'No list prices'}
                </Stat>
                <Stat label="Returns" value={pctText(selected.return_rate)}>{number(selected.returned_units)} units returned</Stat>
              </div>

              <div>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-gray-900">Daily trend</h3>
                  <MetricToggle value={metric} onChange={setMetric} label="Trend metric" />
                </div>
                {hasTrend ? <TrendChart trend={selected.trend} metric={metric} color={color} name={name} /> : (
                  <p className="rounded-lg bg-gray-50 p-4 text-sm text-gray-600">No daily sales recorded on {name} in this period.</p>
                )}
              </div>

              <div>
                <h3 className="mb-1 text-sm font-semibold text-gray-900">Top {topSkus.length || ''} products on {name}</h3>
                {topSkus.length ? (
                  <ol className="space-y-0.5" data-testid="platform-top-skus">
                    {topSkus.map(item => (
                      <li key={item.sku_code}>
                        <ShareBar label={item.product_name || item.sku_code} value={shortMoney(item.revenue)}
                          share={selected.revenue ? (item.revenue / selected.revenue) * 100 : 0} color={color}
                          sublabel={`${item.sku_code} · ${number(item.units)} units`}
                          onClick={() => setOpenSku(item.sku_code)} ariaLabel={`Open sales details for ${item.product_name || item.sku_code} on ${name}`} />
                      </li>
                    ))}
                  </ol>
                ) : <p className="text-sm text-gray-600">No product sales on {name} in this period.</p>}
              </div>
            </div>
          )}
        </section>
      </div>

      {data && (
        <section className={`rounded-xl border border-gray-200 bg-white transition-opacity ${loading ? 'opacity-60' : ''}`} aria-labelledby="platform-compare-title">
          <h2 id="platform-compare-title" className="border-b border-gray-100 px-4 py-3 text-sm font-semibold text-gray-900">
            All platforms compared <span className="font-normal text-gray-500">· changes {vs}</span>
          </h2>
          <div className="relative overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm" data-testid="platform-compare-table">
              <caption className="sr-only">Every platform with sales: revenue, share, change {vs}, units, average price, discount and return rate. Select a platform to see its details.</caption>
              <thead className="bg-gray-50 text-xs text-gray-600">
                <tr>
                  <SortHeader label="Platform" sortKey="name" table={table} />
                  <SortHeader label="Revenue" sortKey="revenue" table={table} align="right" />
                  <SortHeader label="Share" sortKey="share" table={table} align="right" />
                  <SortHeader label="₹ change" sortKey="revenue_delta" table={table} align="right" />
                  <SortHeader label="% change" sortKey="revenue_change" table={table} align="right" />
                  <SortHeader label="Units" sortKey="units" table={table} align="right" />
                  <SortHeader label="Avg price" sortKey="avg_selling_price" table={table} align="right" />
                  <SortHeader label="Avg discount" sortKey="avg_discount_pct" table={table} align="right" />
                  <SortHeader label="Return rate" sortKey="return_rate" table={table} align="right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {table.pageRows.map(item => {
                  const label = item.label || platformLabel(item.platform);
                  const delta = item.revenue_delta ?? (item.revenue - item.previous_revenue);
                  const isSelected = selected?.platform === item.platform;
                  return (
                    <tr key={item.platform} onClick={() => setPicked(item.platform)}
                      className={`cursor-pointer hover:bg-blue-50/40 ${isSelected ? 'bg-blue-50/60' : ''}`} data-testid={`platform-row-${item.platform}`}>
                      <td className="px-3 py-2">
                        <button type="button" onClick={event => { event.stopPropagation(); setPicked(item.platform); }} aria-pressed={isSelected}
                          className="inline-flex items-center gap-2 rounded font-medium text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: platformColor(item.platform) }} aria-hidden="true" />{label}{scope.platform === item.platform && <FilteredTag />}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right font-medium tabular-nums">{shortMoney(item.revenue)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{pctText(item.share)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${delta > 0 ? 'text-green-700' : delta < 0 ? 'text-red-700' : 'text-gray-500'}`}>{delta ? formatDelta(delta) : '—'}</td>
                      <td className="px-3 py-2 text-right"><ChangePct row={item} /></td>
                      <td className="px-3 py-2 text-right tabular-nums">{number(item.units)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{item.avg_selling_price ? wholeMoney(item.avg_selling_price) : '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums"
                        title={item.avg_discount_pct == null ? undefined : item.avg_discount_pct < 0 ? 'Average sale was above list price' : 'Average discount below list price'}>
                        {item.avg_discount_pct == null ? '—' : item.avg_discount_pct < 0 ? `+${pctText(Math.abs(item.avg_discount_pct))} above` : item.avg_discount_pct > 0 ? `−${pctText(item.avg_discount_pct)}` : pctText(0)}
                      </td>
                      <td className={`px-3 py-2 text-right tabular-nums ${item.return_rate >= 10 ? 'text-red-700' : ''}`}>{item.returned_units ? pctText(item.return_rate) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="border-t border-gray-200 bg-gray-50 text-xs font-medium text-gray-700">
                <tr>
                  <td className="px-3 py-2">Total</td>
                  <td className="px-3 py-2 text-right tabular-nums">{shortMoney(data.totals?.revenue)}</td>
                  <td className="px-3 py-2 text-right">100%</td>
                  <td className="px-3 py-2 text-right" colSpan={2}>
                    {data.totals?.previous_revenue != null && <DeltaBadge current={data.totals.revenue} previous={data.totals.previous_revenue} size="xs" />}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{number(data.totals?.units)}</td>
                  <td colSpan={3} aria-hidden="true" />
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      )}

      <ProductDetailDrawer sku={openSku} scope={selected ? { ...scope, platform: selected.platform } : scope}
        onClose={() => setOpenSku(null)} onPlatform={code => setScope({ platform: code })} />
    </div>
  );
}
